import {gql} from "@apollo/client";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../../pagination";
import {AnticipatedDischargeInfo, AnticipatedDischargesFilterValue, asQueryParam} from "./domain";
import {
    Column,
    EnrichedTable,
    LinkToNewTabKeepContext,
    LoadingSpinner,
    RelayConnection,
    SimpleDropdown,
    ErrorDisplay,
    SortBy
} from "../../../common";
import React, {Dispatch, useEffect} from "react";
import {AnticipatedDischargePageAction} from "./AnticipatedDischargePage";
import {SortOrder} from "../domain";
import {useLazyQuery$} from "../../../graphqlsupport";
import {useObservable} from "../../../rxsupport";
import {Pane} from "evergreen-ui";
import {NavigationSearchBar} from "../../../placement/components/NavigationSearchBar";
import {formatDateStr} from "common/util/date";
import {get} from "lodash";
import AnticipatedDischargeFilter from "./AnticipatedDischargeFilter";
import {AnticipatedDischargeModalState, ConfirmedDischargeDateModal} from "./ConfirmedDischargeDateModal";
import {AnticipatedDischargeActionDropdown} from "./AnticipatedDischargeActionDropdown";
import {AnticipatedDischargeListFilterBadge} from "./AnticipatedDischargeListFilterBadge";
import {useAuthAnticipatedDischargeDate$} from "./useSubmitAnticipatedDischargeDate";
import { useApplicationRootUrl } from "common/component/ApplicationRootContext";

export const ANTICIPATED_DISCHARGE_DATE_LIST = gql`
    query anticipatedDischargeDateQuery($first: Int = 20, $after: String, $search: String, $params: AnticipatedDischargeDateQueryParamsInput!) {
        anticipatedDischargeDateQuery(
            first: $first
            after: $after
            search: $search
            params: $params
        ) {
            edges {
                cursor
                node {
                    guid
                    sfId
                    client {
                      guid
                      sfId
                      fullName
                    }
                    parentCase {
                      guid
                      sfId
                      clientMrn
                    }
                    anticipatedDischargeDate
                    providerConfirmedDischargeDate
                    dischargeNotificationDueDate
                    clinicalRationale
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                endCursor
                hasNextPage
                startCursor
            }
        }
    }
`;

type AnticipatedDischargeListProps = {
    pagination: RelayPaginationState;
    filter: AnticipatedDischargesFilterValue;
    modalSelectedItems: AnticipatedDischargeInfo[];
    selectedUnconfirmedADs: AnticipatedDischargeInfo[];
    searchText: string;
    pageSize: number;
    sortBy: SortBy<AnticipatedDischargeInfo>;
    dischargeModalState: AnticipatedDischargeModalState;
    dispatch: Dispatch<AnticipatedDischargePageAction>;
}

type AnticipatedDischargeListQueryResponse = RelayConnection<AnticipatedDischargeInfo> & { totalElements: number };

export const AnticipatedDischargeList = ({
                                             pagination,
                                             filter,
                                             sortBy,
                                             selectedUnconfirmedADs,
                                             dischargeModalState,
                                             modalSelectedItems,
                                             searchText,
                                             pageSize,
                                             dispatch,
                                         }: AnticipatedDischargeListProps) => {
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        first: number;
        after: string;
        search: string;
        params: {
            anticipatedDischargeDateFrom: string | null;
            anticipatedDischargeDateTo: string | null;
            dischargeNotificationDueDateFrom: string | null;
            dischargeNotificationDueDateTo: string | null;
            sortDir: SortOrder,
            sortBy: string,
        }
    }, { anticipatedDischargeDateQuery: AnticipatedDischargeListQueryResponse }>(
        ANTICIPATED_DISCHARGE_DATE_LIST,
        {
            ...pagination.toGraphQlParams(),
            search: searchText,
            params: {
                ...asQueryParam(filter),
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            }
        }
    );
    const rootUrl = useApplicationRootUrl();

    const selectedGuids = selectedUnconfirmedADs.map(v => v.guid);
    const {submitAuthAnticipatedDischargeDate} = useAuthAnticipatedDischargeDate$();

    const startFetch = () => {
        execute().then(() => console.debug("Initial fetch"))
    }

    useEffect(() => {
        startFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, searchText]);

    const loading = useObservable(isRunning$, true);
    const totalAnticipatedDischarges = data?.anticipatedDischargeDateQuery?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalAnticipatedDischarges);
    if (error) {
        return <ErrorDisplay error={error} title="Error loading anticipated discharge dates" />;
    }
    const anticipatedDischarges = data?.anticipatedDischargeDateQuery.edges.map((edge) => edge.node) || [];

    const onlyContainConfirmedAD = anticipatedDischarges.every(v => v.providerConfirmedDischargeDate !== null);

    const isAllSelected = () => anticipatedDischarges
        .every(v => selectedGuids.includes(v.guid)) ?? false;

    return <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col"
                 minHeight="calc(100vh - 100px - 260px - 40px)">
        <ConfirmedDischargeDateModal modalSelectedItems={modalSelectedItems}
                                     dispatch={dispatch}
                                     state={dischargeModalState}
                                     onFinished={async (p) => {
                                         await submitAuthAnticipatedDischargeDate(p);
                                         dispatch({
                                             type: "SET_MULTI_SELECT_TOGGLE_ALL",
                                             items: anticipatedDischarges
                                         })

                                         void execute();
                                     }
                                     }/>
        <Pane display='flex' justifyContent='space-between' padding={2} paddingBottom={8}>
            <div/>
            <div className='flex'>
                <NavigationSearchBar placeholder={"Search by Client Name or MRN"}
                                     searchQuery={searchText}
                                     setSearchQuery={(search) => dispatch({type: 'SET_SEARCH', search: search})}/>
                <AnticipatedDischargeFilter initFilterValue={filter} dispatch={dispatch}/>
                <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                minWidth={"2rem"}
                                textColor={"gray-700"}
                                setSelected={(newPageSize) => dispatch({
                                    type: "SET_PAGE_SIZE",
                                    pageSize: parseInt(newPageSize)
                                })}/>
                <RelayPagination pageInfo={data?.anticipatedDischargeDateQuery.pageInfo}
                                 count={totalAnticipatedDischarges}
                                 onClick={onPaginationClick}/>
            </div>
        </Pane>

        <AnticipatedDischargeListFilterBadge filter={filter} dispatch={dispatch}/>
        <EnrichedTable<AnticipatedDischargeInfo>
            sortBy={sortBy}
            //@ts-ignore
            firstColumn={"client.fullName"}
            setSortBy={(newSortColumn: Column<AnticipatedDischargeInfo>) => {
                let newSortOrder = SortOrder.DESC;
                if (newSortColumn.attr === sortBy.column && sortBy.order === newSortOrder) {
                    newSortOrder = SortOrder.ASC;
                }

                dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn.attr, order: newSortOrder}});
            }}
            styleOverride={{
                customTableStyle: {height: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"},
                tableContent: "overflow-auto flex flex-col flex-grow mb-4 scrollbar-always",
                firstColStyle: "pl-4"
            }}
            columns={[
                //@ts-ignore
                {attr: 'client.fullName', name: 'Client Name', span: 3},
                //@ts-ignore
                {attr: 'parentCase.clientMrn', name: 'MRN', span: 2},
                {
                    attr: 'anticipatedDischargeDate',
                    name: 'Anticipated Discharge Date',
                    span: 2,
                },
                {
                    attr: 'providerConfirmedDischargeDate',
                    name: 'Confirmed Anticipated Discharge Date',
                    span: 2,
                },
                {attr: 'dischargeNotificationDueDate', name: 'Discharge Notification Due Date', span: 3},
            ]}
            rowsData={anticipatedDischarges}
            keyColumn={'guid'}
            cellDisplayFunc={(metric, column) => {
                if (column.attr === 'anticipatedDischargeDate' || column.attr === 'dischargeNotificationDueDate' || column.attr === 'providerConfirmedDischargeDate') {
                    return formatDateStr(get(metric, column.attr, ""));
                }

                if (column.attr === "client.fullName") {
                    return (
                        <LinkToNewTabKeepContext link={`${rootUrl}/clients/${metric?.client?.guid}/contacts`}>
                        <span className={`underline text-blue-portal-link`}>
                                {get(metric, "client.fullName", "")}
                        </span>
                        </LinkToNewTabKeepContext>
                    )
                }

                return get(metric, column.attr, "");
            }}
            emptyMessage={
                <div className='w-full flex items-center'>
                    <div className='m-auto pt-8'>
                        There are no anticipated discharges that match the search criteria
                    </div>
                </div>
            }
            loadingFeat={{
                loading: loading,
                loadingDisplayElement: <LoadingSpinner spacing={20} height={"40vh"}/>
            }}

            multiselectFeat={{
                isSelected: (item) => selectedGuids.includes(item.guid),
                toggleSelect: (item) => dispatch({type: "SET_MULTI_SELECT_TOGGLE", item: item}),
                shouldDisableSelected: (item) => filter.status === "Confirmed" || item.providerConfirmedDischargeDate !== null,
                toggleAllFeat: {
                    isAllSelected: () => isAllSelected() && anticipatedDischarges.length !== 0,
                    isSomeSelected: () => {
                        return (!isAllSelected() && anticipatedDischarges.some(v => selectedGuids.includes(v.guid))) ?? false
                    },
                    disabled: filter.status === "Confirmed" || onlyContainConfirmedAD,
                    toggle: () => dispatch({
                        type: "SET_MULTI_SELECT_TOGGLE_ALL",
                        items: anticipatedDischarges
                    })
                }
            }}
            actionColFeat={
                {
                    colSpan: 2,
                    actionColumn: <AnticipatedDischargeActionDropdown
                        disabled={selectedUnconfirmedADs.length === 0}
                        selectedItems={selectedUnconfirmedADs}
                        dispatch={dispatch}/>
                }
            }
        />
    </Pane>
}
